import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppShell,
  Burger,
  Button,
  Container,
  Flex,
  Group,
  Text,
  Menu,
  Avatar,
  useMantineTheme,
  Box,
  NavLink,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  useCurrentUser,
  useInvalidateCurrentUser,
} from '@shared/hooks/useCurrentUser';
import { getDefaultHeaders } from '@shared/hooks/utils';
import { useErrorLogger } from '@shared/hooks/useErrorLogger';
import { ThemeMenuItems } from '../shared/ThemeMenuItem';
import { ImpersonationNotice } from './ImpersonationNotice';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { data: currentUser, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const headerHeight = theme.other.headerHeight as string;
  const headerHeightWithImpersonation = theme.other
    .headerHeightWithImpersonation as string;
  const footerHeight = theme.other.footerHeight as string;
  const invalidateCurrentUser = useInvalidateCurrentUser();
  const { logError } = useErrorLogger();

  const handleLogout = async () => {
    try {
      await fetch('/users/sign_out', {
        method: 'DELETE',
        headers: getDefaultHeaders(),
        credentials: 'include',
      });

      await invalidateCurrentUser();

      // Clear the auth refresh flag so next login gets fresh tokens
      sessionStorage.removeItem('auth-page-refreshed');

      // Force a full page refresh to update CSRF tokens
      window.location.href = '/auth/login';
    } catch (err) {
      logError(err as Error, {
        type: 'component_error',
        component: 'Layout',
        action: 'logout',
        metadata: { userId: currentUser?.id },
      });
    }
  };

  const handleLoginClick = () => {
    navigate('/auth/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    close();
  };

  const appShellHeaderHeight = currentUser?.impersonation?.isImpersonating
    ? headerHeightWithImpersonation
    : headerHeight;

  return (
    <AppShell
      header={{ height: appShellHeaderHeight }}
      footer={{ height: footerHeight }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !opened, desktop: true },
      }}
    >
      <AppShell.Header>
        {currentUser?.impersonation && (
          <ImpersonationNotice
            impersonationData={currentUser.impersonation}
            onImpersonationStopped={invalidateCurrentUser}
          />
        )}
        <Container size="xl">
          <Flex h={headerHeight} justify="space-between" align="center">
            <Group>
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" />
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Group>
                  <img
                    src="/agilex-logo.png"
                    alt="AgileX Logo"
                    style={{ height: 40 }}
                  />
                  <Text
                    fz="xl"
                    fw={700}
                    style={{ color: theme.colors.brand[6] }}
                  >
                    AgileX
                  </Text>
                </Group>
              </Link>
              {currentUser?.email && (
                <Group ml="xl" visibleFrom="sm">
                  <Link to="/surveys" style={{ textDecoration: 'none' }}>
                    <Text fz="sm" fw={500}>
                      Surveys
                    </Text>
                  </Link>
                  {currentUser.permissions?.canViewTeams && (
                    <Link to="/teams" style={{ textDecoration: 'none' }}>
                      <Text fz="sm" fw={500}>
                        Teams
                      </Text>
                    </Link>
                  )}
                  {currentUser.permissions?.canManageSurveyInvitations && (
                    <Link
                      to="/survey-invitations"
                      style={{ textDecoration: 'none' }}
                    >
                      <Text fz="sm" fw={500}>
                        Survey Invitations
                      </Text>
                    </Link>
                  )}
                  {currentUser.permissions?.canViewTeamSurveyResults && (
                    <Link
                      to="/team-survey-results"
                      style={{ textDecoration: 'none' }}
                    >
                      <Text fz="sm" fw={500}>
                        Team Survey Results
                      </Text>
                    </Link>
                  )}
                </Group>
              )}
            </Group>

            <Group>
              {isLoading ? (
                <Text>Loading...</Text>
              ) : currentUser?.email ? (
                <Menu position="bottom-end" shadow="md">
                  <Menu.Target>
                    <Group style={{ cursor: 'pointer' }}>
                      <Text fz="sm" fw={500} visibleFrom="sm">
                        {currentUser.displayName}
                      </Text>
                      <Avatar radius="xl" color="brand">
                        {(currentUser.displayName?.[0] || '').toUpperCase()}
                      </Avatar>
                    </Group>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>{currentUser.email}</Menu.Label>
                    <Menu.Divider />
                    <Menu.Item onClick={() => navigate('/settings/profile')}>
                      Profile Settings
                    </Menu.Item>
                    {currentUser.permissions?.canManageOrganizationSettings && (
                      <Menu.Item
                        onClick={() => navigate('/settings/organization')}
                      >
                        Organization Settings
                      </Menu.Item>
                    )}
                    {currentUser.permissions?.canManageSurveyAccess && (
                      <Menu.Item onClick={() => navigate('/survey-access')}>
                        Survey Access Management
                      </Menu.Item>
                    )}
                    {currentUser.permissions?.canManageTeamAccess && (
                      <Menu.Item onClick={() => navigate('/team-access')}>
                        Team Access Management
                      </Menu.Item>
                    )}
                    <Menu.Divider />
                    <ThemeMenuItems />
                    <Menu.Divider />
                    <Menu.Item onClick={handleLogout} color="red">
                      Logout
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              ) : (
                <Button onClick={handleLoginClick}>Sign In</Button>
              )}
            </Group>
          </Flex>
        </Container>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        {currentUser?.email && (
          <Box p="md">
            <Text fz="lg" fw={700} mb="xl">
              Menu
            </Text>
            <NavLink
              label="Surveys"
              onClick={() => handleNavigation('/surveys')}
              active={window.location.pathname.startsWith('/surveys')}
            />
            {currentUser.permissions?.canViewTeams && (
              <NavLink
                label="Teams"
                onClick={() => handleNavigation('/teams')}
                active={window.location.pathname.startsWith('/teams')}
              />
            )}
            {currentUser.permissions?.canManageSurveyInvitations && (
              <NavLink
                label="Survey Invitations"
                onClick={() => handleNavigation('/survey-invitations')}
                active={window.location.pathname.startsWith(
                  '/survey-invitations'
                )}
              />
            )}
            {currentUser.permissions?.canViewTeamSurveyResults && (
              <NavLink
                label="Team Survey Results"
                onClick={() => handleNavigation('/team-survey-results')}
                active={window.location.pathname.startsWith(
                  '/team-survey-results'
                )}
              />
            )}
            <NavLink
              label="Profile Settings"
              onClick={() => handleNavigation('/settings/profile')}
              active={window.location.pathname === '/settings/profile'}
            />
            {currentUser.permissions?.canManageOrganizationSettings && (
              <NavLink
                label="Organization Settings"
                onClick={() => handleNavigation('/settings/organization')}
                active={window.location.pathname === '/settings/organization'}
              />
            )}
            <NavLink label="Logout" color="red" onClick={handleLogout} />
          </Box>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <Container size="xl" py="xl">
          {children}
        </Container>
      </AppShell.Main>

      <AppShell.Footer p="md">
        <Container size="xl">
          <Flex justify="space-between" align="center">
            <Text fz="sm" c="dimmed">
              © 2025 AgileX. All rights reserved.
            </Text>
            <Group gap="md">
              <Text component="a" href="#" fz="sm">
                Privacy
              </Text>
              <Text component="a" href="#" fz="sm">
                Terms
              </Text>
              <Text component="a" href="#" fz="sm">
                Contact
              </Text>
            </Group>
          </Flex>
        </Container>
      </AppShell.Footer>
    </AppShell>
  );
}
